"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Robert Sterling",
  position: "Estate Planning Client",
  quote: "LIFECOVER provided more than just a policy; they gave my family a future secured by professional integrity and genuine care.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1691035/img/client_robert_sterling_portrait.png",
  initials: "RS"
}, {
  id: 2,
  author: "Elena Vance",
  position: "Business Owner",
  quote: "The simplicity of their process belies the depth of their expertise. I finally feel my business and legacy are in safe hands.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1691035/img/client_elena_vance_portrait.png",
  initials: "EV"
}, {
  id: 3,
  author: "Marcus Thorne",
  position: "Family Patriarch",
  quote: "In an industry of complexity, LIFECOVER stands out for their clarity and the profound peace of mind they've delivered to my heirs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1691035/img/client_marcus_thorne_portrait.png",
  initials: "MT"
}];
export default function PeaceOfMindForOurClients() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [visible, setVisible] = useState(true);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const goTo = useCallback((index: number, dir: "next" | "prev") => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setVisible(false);
    setTimeout(() => {
      setCurrent(index);
      setVisible(true);
      setAnimating(false);
    }, 400);
  }, [animating]);
  const handlePrev = () => {
    const prev = (current - 1 + testimonials.length) % testimonials.length;
    goTo(prev, "prev");
  };
  const handleNext = () => {
    const next = (current + 1) % testimonials.length;
    goTo(next, "next");
  };
  const handleDot = (index: number) => {
    if (index === current) return;
    goTo(index, index > current ? "next" : "prev");
  };
  const item = testimonials[current];
  return <section data-section-id="230596" id="peace-of-mind-for-our-clients" className="bg-background text-foreground py-20 md:py-28 overflow-hidden" style={{
    background: "var(--background)"
  }}>
      <div className="max-w-6xl mx-auto px-6">
        {}
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-default text-xs tracking-widest uppercase mb-3" style={{
          color: "var(--secondary)"
        }}>
            Client Experiences
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={{
          color: "var(--foreground)"
        }}>
            Peace of Mind for Our Clients
          </h2>
          <div className="mx-auto mt-5 h-px w-16" style={{
          background: "var(--secondary)"
        }} />
        </div>

        {}
        <div className="relative mx-auto max-w-5xl">
          {}
          <div className="rounded-lg shadow-xl overflow-hidden" style={{
          background: "var(--card)",
          border: "1px solid var(--border)"
        }}>
            <div className={`flex flex-col md:flex-row min-h-[420px] transition-all duration-400 ${visible ? "opacity-100 translate-x-0" : direction === "next" ? "opacity-0 translate-x-8" : "opacity-0 -translate-x-8"}`} style={{
            transition: "opacity 0.4s ease, transform 0.4s ease"
          }}>
              {}
              <div className="relative md:w-2/5 h-64 md:h-auto overflow-hidden flex-shrink-0" style={{
              background: "var(--muted)"
            }}>
                <img src={item.image} alt={item.author} className="w-full h-full object-cover object-center grayscale hover:grayscale-0 transition-all duration-700" style={{
                minHeight: "260px"
              }} onError={e => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }} />
                {}
                <div className="absolute inset-0 flex items-center justify-center opacity-0">
                  <SafeHydrate><Avatar className="size-24">
                    <AvatarFallback className="text-2xl font-default font-semibold" style={{
                      background: "var(--accent)",
                      color: "var(--primary)"
                    }}>
                      {item.initials}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                </div>
              </div>

              {}
              <div className="flex-1 flex flex-col justify-center px-8 md:px-12 py-10 md:py-14 relative">
                {}
                <div className="mb-6">
                  <Quote className="size-10 md:size-12 opacity-10" style={{
                  color: "var(--secondary)"
                }} strokeWidth={1} />
                </div>

                {}
                <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl leading-relaxed mb-8 italic" style={{
                color: "var(--foreground)",
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontWeight: 400,
                letterSpacing: "-0.01em"
              }}>
                  &ldquo;{item.quote}&rdquo;
                </blockquote>

                {}
                <div className="flex items-center gap-4">
                  <div className="h-px w-8 flex-shrink-0" style={{
                  background: "var(--secondary)"
                }} />
                  <div>
                    <p className="font-default font-semibold text-base tracking-wide" style={{
                    color: "var(--foreground)"
                  }}>
                      {item.author}
                    </p>
                    <p className="font-default text-sm mt-0.5 uppercase tracking-widest opacity-70" style={{
                    color: "var(--muted-foreground)"
                  }}>
                      {item.position}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex items-center justify-center gap-6 mt-10">
            {}
            <Button variant="outline" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="rounded-lg size-11 border transition-all duration-200 hover:bg-accent" style={{
            borderColor: "var(--border)",
            background: "var(--background)",
            color: "var(--foreground)"
          }}>
              <ChevronLeft className="size-4 stroke-1" />
            </Button>

            {}
            <div className="flex items-center gap-2">
              {testimonials.map((t, i) => <Button data-index={i} key={t.id} onClick={() => handleDot(i)} aria-label={`Go to testimonial ${i + 1}`} variant="ghost" className="transition-all duration-300 rounded-lg p-0 h-auto w-auto focus:outline-none hover:bg-transparent" style={{
              width: i === current ? "24px" : "8px",
              height: "2px",
              background: i === current ? "var(--primary)" : "var(--border)",
              border: "none",
              cursor: "pointer"
            }} />)}
            </div>

            {}
            <Button variant="outline" size="icon" onClick={handleNext} aria-label="Next testimonial" className="rounded-lg size-11 border transition-all duration-200 hover:bg-accent" style={{
            borderColor: "var(--border)",
            background: "var(--background)",
            color: "var(--foreground)"
          }}>
              <ChevronRight className="size-4 stroke-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}